import { Role } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Popover } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Trash2 } from 'lucide-react';
import React, { RefObject, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.css';

const ICON_SIZE = 14;

interface ActionsCellProps {
  email: string;
  userName: string;
  roles: Role[];
  containerRef: RefObject<HTMLDivElement | null>;
  onRemove: (params: { email: string; userName: string; roles: Role[] }) => void;
}

function ActionsCellComponent({ email, userName, roles, containerRef, onRemove }: ActionsCellProps) {
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleConfirmRemove = useCallback(() => {
    setIsPopoverOpen(false);
    onRemove({ email, userName, roles });
  }, [email, userName, roles, onRemove]);

  return (
    <Stack
      alignItems="center"
      className={styles.actionButtonContainer}
      gap={2}
      justifyContent="center"
      orientation="horizontal"
    >
      <Popover
        content={
          <Stack className={styles.popoverContent} gap={3} orientation="vertical">
            <Text as="p" className={getTextClassName('sm')}>
              {t('orgs:confirm_remove_user', { name: userName })}
            </Text>

            <Stack alignItems="center" gap={3} orientation="horizontal">
              <Button data-testid={`confirm-remove-${email}`} onPress={handleConfirmRemove} size="xs" visual="danger">
                {t('general:remove')}
              </Button>

              <Button
                data-testid={`cancel-remove-${email}`}
                onPress={() => setIsPopoverOpen(false)}
                size="xs"
                visual="outline"
              >
                {t('general:cancel')}
              </Button>
            </Stack>
          </Stack>
        }
        isOpen={isPopoverOpen}
        onClickOutside={() => setIsPopoverOpen(false)}
        parentElement={containerRef.current || undefined}
        align="end"
        positions={['left', 'right']}
      >
        <Button data-testid={`remove-user-${email}`} onPress={() => setIsPopoverOpen(true)} size="xs" visual="danger">
          <Trash2 size={ICON_SIZE} />
        </Button>
      </Popover>
    </Stack>
  );
}

const ActionsCell = memo(ActionsCellComponent);

ActionsCell.displayName = 'ActionsCell';

export default ActionsCell;
